import React, { useState, useEffect } from "react";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import "../../../assets/custom-css/curriculum.css";
import CustomButton from "../../../components/shared/button/CustomButton";
import CommonTable from "../../../components/shared/table/Table";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Row, Col, Empty } from "antd";
import Back from "../../../assets/back-btn.svg";
import { DownloadOutlined } from "@ant-design/icons";
import CustomTab from "../../../components/shared/tab/TabComponent";
import searchIcon from "../../../assets/Search-Icon.svg";
import { getBundleKpiReport, downloadBundleKpiReport } from "../../../service/ReportingService";
import { CurriculumReportColumns } from "../../../assets/FormFeilds/CurriculumField";
import useBundleReport from "../../../assets/hooks/CurriculumHooks/useBundleReport";
import { tabList } from "../../../constants/constants";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../components/shared/pagination/messages";
import CustomInput from "../../../components/global/CustomInput/CustomInput";
import { createSearchDebounce } from "../../../assets/commonFunctions/CommonFunction";
import PageLoader from "../../../components/global/Loader/PageLoader";

const CurriculumReport = () => {
  const tableRef = React.useRef(null);
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [searchParams] = useSearchParams();
  const defaultStatus = searchParams.get("status") || "active";

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [status, setStatus] = useState(defaultStatus);
  const isActive = status === "active";

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    totalElements,
    setTotalElements,
    setPageSize,
  } = usePagination();

  const { data, error, fetchBundleKpiReport } = useBundleReport();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetchBundleKpiReport(
        currentPage - 1,
        pageSize,
        isActive,
        searchText,
      );
      if (response?.success) {
        setTotalElements(response.data.total);
      }
    } catch (err) {
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return createSearchDebounce(() => {
      const query = searchText?.trim() || "";
      fetchData(currentPage, pageSize, isActive, query);
    });
  }, [searchText, currentPage, pageSize, isActive]);

  const sanitizeData = (data) =>
    JSON.parse(
      JSON.stringify(data, (_, val) =>
        val === null || val === undefined ? "null" : val,
      ),
    );

  const downloadReport = async () => {
    setShowLoader(true);
    try {
      await downloadBundleKpiReport(isActive, searchText);
      showSuccessMessage("Report Downloaded Successfully!");
    } catch (error) {
      showErrorMessage(error?.message || "Failed to Download Report");
    } finally {
      setShowLoader(false);
    }
  };


  const handleSearch = (query) => {
    setSearchText(query);
    if (query && currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleTableChange = (pagination) => {
    if (pagination) {
      if (pagination.pageSize !== pageSize) {
        setPageSize(pagination.pageSize);
        setCurrentPage(1);
      } else {
        setCurrentPage(pagination.current);
      }
      const scrollableNode = tableRef.current?.querySelector(".ant-table-body");
      if (scrollableNode) {
        scrollableNode.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  };

  const handleRowClick = (record) => {
    navigate(`/bundles-history/${record.bundleId}`, { state: { bundleName: record.bundleName } });
  };

  const handleViewBundleClick = (bundleId, bundleName) => {
    navigate(`/bundles-history/${bundleId}`, { state: { bundleName } });
  };

  const handleTabChange = (key) => {
    setStatus(key);
    setCurrentPage(1);
    setSearchText("");
  };

  return (
    <>
      <PageLoader visible={showLoader} />
      <div className="p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)] gap-6">
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <h1 className="text-[#e74860] text-[1.75rem] font-normal">
              Curriculum Global Report
            </h1>
            <div
              className="flex gap-2 justify-center cursor-pointer"
              onClick={() => navigate(-1)}
            >
              <img alt="back-btn" src={Back} />
              <span>Back</span>
            </div>
          </div>

          <div>
            <Row>
              <Col span={10}>
                <CustomTab
                  entries={tabList}
                  activeKey={status}
                  onChange={handleTabChange}
                />
              </Col>

              <Col span={14}>
                <div className="flex gap-[10px] justify-end">
                  <CustomInput
                    key={status}
                    icon={
                      <img src={searchIcon} alt="search" className="w-6 h-6" />
                    }
                    placeholder="Search"
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="!w-[280px]"
                  />

                  <CustomButton
                    icon={<DownloadOutlined />}
                    onClick={downloadReport}
                    variant="primary"
                  >
                    Export
                  </CustomButton>
                </div>
              </Col>

              <Row className="w-full">
                <div className="w-full" ref={tableRef}>
                  <CommonTable
                    data={data}
                    columns={CurriculumReportColumns(handleViewBundleClick)}
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: totalElements,
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    scroll={{ y: "60vh" }}
                    serverSide={true}
                    rowClickable={true}
                    settingModalDataFunction={handleRowClick}
                    hover={true}
                    rowKey={(record) => record.bundleId}
                    showLoader={loading}
                  />
                </div>
              </Row>
            </Row>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminHOC(CurriculumReport);
